import type {
  AdminDeskReview,
  AdminProgrammeItem,
  AdminReviewListItem,
  AmbassadorApplication,
  Archetype,
  AssessmentFixture,
  AssessmentFixtureEvalSummary,
  AssessmentFixtureRun,
  AssessmentFixtureWithRuns,
  AssessmentRunStatus,
  Assignment,
  AssignmentMappingResponse,
  AvailableUser,
  BadgeArtwork,
  CertificateAuthoriseResult,
  CertificateRun,
  CertificateRunPreview,
  CertificateSendLogItem,
  CohortAssessor,
  CohortAuditEntry,
  CohortBadges,
  ContactSubmission,
  CreateCheckoutSessionResponse,
  DeskProfile,
  DeskProfileUpdate,
  DeskReviewAction,
  DeskReviewCreate,
  DeskReviewModerationStatus,
  DeskReviewStatus,
  DeskReviewSummary,
  DominionDemoAccess,
  EligibleAssessor,
  EmailLogItem,
  EmailTemplateDetail,
  EmailTemplateListItem,
  EmailTimelineResponse,
  EnrollmentWithUser,
  EventPitch,
  FaqEntry,
  FreemiumDiscount,
  GenerationJob,
  MoveDirection,
  MyCertificate,
  MyPortfolio,
  MyPortfolioUpdatePayload,
  MySupportRequest,
  NewsletterSubscriber,
  Notification,
  NotificationListResponse,
  Organization,
  Page,
  ParticipantCohortItem,
  ParticipantTopicItem,
  PartnershipApplication,
  Payment,
  Person,
  PersonGroup,
  PersonPayload,
  PortfolioSkillOptions,
  ProductCode,
  ProductEntitlements,
  Programme,
  ProgrammeLockReadiness,
  ProgrammeWithAssignments,
  PublicDeskReport,
  PublicFaq,
  RedeemCodeResponse,
  Review,
  ReviewAuditEntry,
  ScheduledEmailItem,
  SchedulePreview,
  ScheduleWindowInput,
  SeedEvent,
  SeedEventStatus,
  SeedEventType,
  Source,
  Submission,
  SupportRequest,
  SupportRequestAdminItem,
  SupportRequestCreatePayload,
  SupportRequestResolution,
  SupportRequestSeverity,
  SupportRequestStatus,
  SupportSettings,
  SupportSettingsUpdate,
  SuppressionItem,
  TagResponse,
  TimelineResponse,
  Topic,
  TopicGenerateRequest,
  UnlockScreenContent,
  UserAssignment,
  UserProfile,
  UserProgramme,
  UserRole,
  UserWithEnrollment,
  WaitlistEntry,
} from "@/types";

const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000/api";

export const SERVICE_UNAVAILABLE_EVENT = "api:service-unavailable";

function notifyServiceUnavailable() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(SERVICE_UNAVAILABLE_EVENT));
  }
}

/** Standard query params for paginated admin list endpoints. */
export interface ListParams {
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: "asc" | "desc";
  q?: string;
}

/** Build a `?page=…&q=…` query string from ListParams (empty string when none). */
function buildListQuery(params?: ListParams): string {
  if (!params) return "";
  const qs = new URLSearchParams();
  if (params.page !== undefined) qs.append("page", params.page.toString());
  if (params.page_size !== undefined) qs.append("page_size", params.page_size.toString());
  if (params.sort_by) qs.append("sort_by", params.sort_by);
  if (params.sort_order) qs.append("sort_order", params.sort_order);
  if (params.q) qs.append("q", params.q);
  const s = qs.toString();
  return s ? `?${s}` : "";
}

class ApiClient {
  private baseURL: string;
  private getToken?: () => Promise<string | null>;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Set the token getter function (should return Clerk JWT token)
   * Call this from a component that has access to useAuth()
   */
  setTokenGetter(getter: () => Promise<string | null>) {
    this.getToken = getter;
  }

  async getAuthToken(): Promise<string | null> {
    return this.getToken ? this.getToken() : null;
  }

  private async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // Get JWT token if available
    const token = this.getToken ? await this.getToken() : null;

    let response: Response;
    try {
      response = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
      });
    } catch (networkError) {
      // fetch throws on network failure / DNS error / API fully down
      notifyServiceUnavailable();
      throw networkError;
    }

    if (!response.ok) {
      if (response.status >= 502 && response.status <= 504) {
        notifyServiceUnavailable();
      }
      const error = await response.json().catch(() => ({
        detail: response.statusText,
      }));
      // FastAPI 422s return `detail` as an array of validation objects, and some
      // endpoints return an object; flatten either to a readable string so the
      // UI never shows "[object Object]".
      const { detail } = error;
      let message: string;
      if (Array.isArray(detail)) {
        message = detail.map((d) => d?.msg ?? JSON.stringify(d)).join("; ");
      } else if (detail && typeof detail === "object") {
        message = detail.message ?? JSON.stringify(detail);
      } else {
        message = detail || "API request failed";
      }
      const err = new Error(message) as Error & { status: number };
      err.status = response.status;
      throw err;
    }

    return response.json();
  }

  // Dev features
  async getDevFeatures(): Promise<{ dev_features_enabled: boolean }> {
    return this.request<{ dev_features_enabled: boolean }>("/dev-info");
  }

  // Organizations
  async getOrganizations(): Promise<Organization[]> {
    return this.request<Organization[]>("/admin/organizations/");
  }

  async createOrganization(name: string): Promise<Organization> {
    return this.request<Organization>("/admin/organizations/", {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  // Users
  async getUserByClerkId(clerkUserId: string): Promise<UserProfile | null> {
    try {
      return await this.request<UserProfile>(`/users/clerk/${clerkUserId}`);
    } catch (error) {
      // Return null if user not found (404)
      if (error instanceof Error && (error as any).status === 404) {
        return null;
      }
      throw error;
    }
  }

  async createUser(data: {
    clerk_user_id: string;
    username: string;
    role?: string;
    organization_id?: string;
    email?: string;
    first_name?: string;
    last_name?: string;
  }): Promise<UserProfile> {
    return this.request<UserProfile>("/users/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  /** Mirror the name into our profile after Clerk has accepted it. */
  async updateMyIdentity(data: { first_name: string; last_name: string }): Promise<UserProfile> {
    return this.request<UserProfile>("/users/me/identity", {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getUsers(): Promise<UserProfile[]> {
    return this.request<UserProfile[]>("/users/");
  }

  async getUsersWithEnrollments(params?: {
    page?: number;
    page_size?: number;
    sort_by?: "email" | "created_at" | "role";
    sort_order?: "asc" | "desc";
    q?: string;
    role?: UserRole;
  }): Promise<Page<UserWithEnrollment>> {
    const queryParams = new URLSearchParams();

    if (params?.page !== undefined) queryParams.append("page", params.page.toString());
    if (params?.page_size !== undefined)
      queryParams.append("page_size", params.page_size.toString());
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.q) queryParams.append("q", params.q);
    if (params?.role) queryParams.append("role", params.role);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/users/with-enrollments?${queryString}`
      : "/users/with-enrollments";

    return this.request<Page<UserWithEnrollment>>(endpoint);
  }

  // LEGACY: Will be removed after migration
  async getUserActiveEnrollment(clerkUserId: string): Promise<{
    id: string;
    user_id: string;
    programme_id: string;
    enrolled_at: string;
    status: string;
    current_week: number;
    total_score: number | null;
    submissions_count: number;
    reviews_count: number;
    programme: {
      id: string;
      name: string;
      status: string;
      start_date: string;
      end_date: string;
      total_weeks: number;
    };
  } | null> {
    try {
      const response = await fetch(`${this.baseURL}/users/clerk/${clerkUserId}/active-enrollment`, {
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error("Failed to fetch enrollment");
      }
      const data = await response.json();
      return data;
    } catch {
      return null;
    }
  }

  // ============================================================================
  // NEW JWT-AUTHENTICATED ENDPOINTS (/me routes)
  // ============================================================================

  /**
   * Get current user's profile (requires JWT token)
   */
  async getMyProfile(): Promise<UserProfile> {
    return this.request<UserProfile>("/users/me");
  }

  async dismissWelcome(): Promise<UserProfile> {
    return this.request<UserProfile>("/users/me/dismiss-welcome", { method: "POST" });
  }

  /**
   * Get current user's enrollments (requires JWT token)
   * @param status Filter by status (default: "active")
   */
  async getMyEnrollments(status: string = "active"): Promise<UserProgramme[]> {
    return this.request<UserProgramme[]>(`/users/me/enrollments?status=${status}`);
  }

  /**
   * Get current user's active enrollment (requires JWT token)
   * Returns preferred programme if set, otherwise most recent active enrollment
   */
  async getMyActiveEnrollment(): Promise<
    {
      id: string;
      user_id: string;
      programme_id: string;
      enrolled_at: string;
      status: string;
      current_week: number;
      total_score: number | null;
      submissions_count: number;
      reviews_count: number;
      programme: {
        id: string;
        name: string;
        status: string;
        start_date: string;
        end_date: string;
        total_weeks: number;
        is_freemium?: boolean;
      };
      unlock_screen_dismissed_at?: string | null;
    } & ProductEntitlements
  > {
    return this.request(`/users/me/active-enrollment`);
  }

  /**
   * Set current user's preferred programme (requires JWT token)
   * @param programmeId The programme ID to set as preferred
   */
  async setMyPreferredProgramme(programmeId: string): Promise<{
    message: string;
    preferred_programme_id: string;
  }> {
    return this.request(`/users/me/set-preferred-programme`, {
      method: "PATCH",
      body: JSON.stringify({ programme_id: programmeId }),
    });
  }

  // The Desk's required profile (KC-507)
  async getDeskProfile(): Promise<DeskProfile> {
    return this.request<DeskProfile>("/desk-profile/me");
  }

  async saveDeskProfile(payload: DeskProfileUpdate): Promise<DeskProfile> {
    return this.request<DeskProfile>("/desk-profile/me", {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  // The Desk review form (KC-508)
  async getDeskReview(): Promise<DeskReviewStatus> {
    return this.request<DeskReviewStatus>("/desk-review/me");
  }

  async submitDeskReview(payload: DeskReviewCreate): Promise<DeskReviewStatus> {
    return this.request<DeskReviewStatus>("/desk-review", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  /** The public Report behind a published Desk review (KC-510); null once it is not published. */
  async getPublicDeskReport(reviewId: string): Promise<PublicDeskReport | null> {
    try {
      return await this.request<PublicDeskReport>(
        `/public/desk-reviews/${encodeURIComponent(reviewId)}/report`
      );
    } catch {
      return null;
    }
  }

  // Desk review moderation (KC-509)
  async getAdminDeskReviews(params: {
    status: DeskReviewModerationStatus;
    page: number;
    page_size: number;
  }): Promise<Page<AdminDeskReview>> {
    const query = new URLSearchParams({
      status: params.status,
      page: String(params.page),
      page_size: String(params.page_size),
    });
    return this.request<Page<AdminDeskReview>>(`/admin/desk-reviews?${query}`);
  }

  async getDeskReviewSummary(): Promise<DeskReviewSummary> {
    return this.request<DeskReviewSummary>("/admin/desk-reviews/summary");
  }

  async moderateDeskReview(id: string, action: DeskReviewAction): Promise<AdminDeskReview> {
    return this.request<AdminDeskReview>(`/admin/desk-reviews/${id}/${action}`, { method: "POST" });
  }

  async setApprover(
    userId: string,
    isApprover: boolean
  ): Promise<{ id: string; is_approver: boolean }> {
    return this.request(`/admin/users/${userId}/approver`, {
      method: "PUT",
      body: JSON.stringify({ is_approver: isApprover }),
    });
  }

  // Portfolio settings (KC-417) — tailor & publish/consent for a programme's portfolio
  async getMyPortfolio(programmeId: string): Promise<MyPortfolio | null> {
    try {
      return await this.request<MyPortfolio>(`/portfolios/me/${programmeId}`);
    } catch (error) {
      if (error instanceof Error && (error as any).status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getPortfolioSkillOptions(): Promise<PortfolioSkillOptions> {
    return this.request<PortfolioSkillOptions>(`/portfolios/skills`);
  }

  async updateMyPortfolio(
    programmeId: string,
    payload: MyPortfolioUpdatePayload
  ): Promise<MyPortfolio> {
    return this.request<MyPortfolio>(`/portfolios/me/${programmeId}`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
  }

  async uploadMyPortfolioPhoto(programmeId: string, file: File): Promise<MyPortfolio> {
    const fd = new FormData();
    fd.append("file", file);
    const token = this.getToken ? await this.getToken() : null;
    const res = await fetch(`${this.baseURL}/portfolios/me/${programmeId}/photo`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Failed to upload photo");
    }
    return res.json();
  }

  async uploadMyPortfolioCv(programmeId: string, file: File): Promise<MyPortfolio> {
    const fd = new FormData();
    fd.append("file", file);
    const token = this.getToken ? await this.getToken() : null;
    const res = await fetch(`${this.baseURL}/portfolios/me/${programmeId}/cv`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Failed to upload CV");
    }
    return res.json();
  }

  async publishMyPortfolio(programmeId: string, consent: boolean): Promise<MyPortfolio> {
    return this.request<MyPortfolio>(`/portfolios/me/${programmeId}/publish`, {
      method: "POST",
      body: JSON.stringify({ consent }),
    });
  }

  async unpublishMyPortfolio(programmeId: string): Promise<MyPortfolio> {
    return this.request<MyPortfolio>(`/portfolios/me/${programmeId}/unpublish`, {
      method: "POST",
    });
  }

  // Dashboard
  async getDashboard(userId: string): Promise<
    {
      programme: {
        id: string;
        name: string;
        status: string;
        start_date: string;
        end_date: string;
        total_weeks: number;
        current_week: number;
      };
      assignments: Array<{
        id: string;
        week_number: number;
        assignment_number: number;
        start_date: string;
        deadline: string;
        time_status: string;
        topic_title: string | null;
        submission: { id: string; submitted_at: string } | null;
        review: {
          id: string;
          status: string;
          final_score: number;
          sections: Record<string, any> | null;
        } | null;
      }>;
      stats: {
        total_score: number | null;
        submissions_count: number;
        reviews_count: number;
      };
    } & ProductEntitlements
  > {
    return this.request(`/users/${userId}/dashboard`);
  }

  // Programmes (admin CRUD)
  async getProgrammes(params?: {
    page?: number;
    page_size?: number;
    q?: string;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    view?: "active" | "completed" | "archived" | "all";
  }): Promise<Page<Programme>> {
    const qs = new URLSearchParams();
    if (params?.page != null) qs.set("page", String(params.page));
    if (params?.page_size != null) qs.set("page_size", String(params.page_size));
    if (params?.q) qs.set("q", params.q);
    if (params?.sort_by) qs.set("sort_by", params.sort_by);
    if (params?.sort_order) qs.set("sort_order", params.sort_order);
    if (params?.view) qs.set("view", params.view);
    const query = qs.toString();
    return this.request<Page<Programme>>(`/admin/programmes/${query ? `?${query}` : ""}`);
  }

  async getProgramme(programmeId: string): Promise<ProgrammeWithAssignments> {
    return this.request<ProgrammeWithAssignments>(`/admin/programmes/${programmeId}`);
  }

  async createProgramme(data: {
    name: string;
    start_date: string;
    // Schedule seed: duration D (days open->due) + count N (total assignments).
    assignment_duration_days?: number;
    assignment_count?: number;
    // Wall-clock time (HH:MM, app timezone) each assignment opens and closes.
    open_time?: string;
    is_draft?: boolean;
    is_private?: boolean;
    price?: number;
    seats_limit?: number | null;
    // Submission attempts per work package, first submission included (KC-451).
    max_submissions?: number;
    // Puts the Cohort on the Dominion Free product (KC-495).
    is_freemium?: boolean;
    // The Product select (KC-507); wins over is_freemium.
    product?: ProductCode;
  }): Promise<ProgrammeWithAssignments> {
    return this.request<ProgrammeWithAssignments>("/admin/programmes/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Cohort schedule editor (KC-375 / KC-378): the hybrid per-window editor.
  async previewSchedule(
    programmeId: string,
    windows: ScheduleWindowInput[]
  ): Promise<SchedulePreview> {
    return this.request<SchedulePreview>(`/admin/programmes/${programmeId}/schedule/preview`, {
      method: "POST",
      body: JSON.stringify({ windows }),
    });
  }

  async applySchedule(
    programmeId: string,
    windows: ScheduleWindowInput[],
    confirm?: string
  ): Promise<ProgrammeWithAssignments> {
    return this.request<ProgrammeWithAssignments>(`/admin/programmes/${programmeId}/schedule`, {
      method: "PUT",
      body: JSON.stringify({ windows, confirm }),
    });
  }

  async updateProgramme(programmeId: string, data: Partial<Programme>): Promise<Programme> {
    return this.request<Programme>(`/admin/programmes/${programmeId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteProgramme(programmeId: string): Promise<void> {
    return this.request<void>(`/admin/programmes/${programmeId}`, {
      method: "DELETE",
    });
  }

  // Cohort lock (KC-287)
  async getProgrammeLockReadiness(programmeId: string): Promise<ProgrammeLockReadiness> {
    return this.request<ProgrammeLockReadiness>(`/admin/programmes/${programmeId}/lock-readiness`);
  }

  async lockProgramme(programmeId: string): Promise<Programme> {
    return this.request<Programme>(`/admin/programmes/${programmeId}/lock`, { method: "POST" });
  }

  /** Copy a cohort as a Draft starting the Monday after it (KC-499). */
  async duplicateProgramme(programmeId: string): Promise<Programme> {
    return this.request<Programme>(`/admin/programmes/${programmeId}/duplicate`, {
      method: "POST",
    });
  }

  /** Superadmin-only: re-opens a locked cohort. Surfaces a 403 for plain admins. */
  async unlockProgramme(programmeId: string): Promise<Programme> {
    return this.request<Programme>(`/admin/programmes/${programmeId}/unlock`, { method: "POST" });
  }

  /** Superadmin-only: cohort lock/unlock/override audit trail. */
  async getProgrammeAuditLog(programmeId: string): Promise<CohortAuditEntry[]> {
    return this.request<CohortAuditEntry[]>(`/admin/programmes/${programmeId}/audit-log`);
  }

  async getNextAvailableProgramme(): Promise<Programme | null> {
    try {
      const response = await fetch(`${this.baseURL}/programmes/next-available`, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        throw new Error("Failed to fetch next programme");
      }
      const data = await response.json();
      return data;
    } catch {
      return null;
    }
  }

  async getAvailableProgrammes(userId?: string): Promise<Programme[]> {
    const params = userId ? `?user_id=${userId}` : "";
    return this.request<Programme[]>(`/programmes/available${params}`);
  }

  /** Resolves a single cohort by id for a direct/internal share link — unlike
   * getAvailableProgrammes, this also resolves private (invite-only) cohorts
   * (KC-389). Returns null if the id doesn't exist or isn't currently joinable. */
  async getProgrammeById(programmeId: string, userId?: string): Promise<Programme | null> {
    const params = userId ? `?user_id=${userId}` : "";
    try {
      return await this.request<Programme>(`/programmes/${programmeId}${params}`);
    } catch {
      return null;
    }
  }

  async joinProgramme(programmeId: string, userId: string): Promise<UserProgramme> {
    return this.request<UserProgramme>(`/programmes/${programmeId}/join`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async updateEnrollmentStatus(
    programmeId: string,
    enrollmentId: string,
    status: "active" | "completed" | "dropped"
  ): Promise<UserProgramme> {
    return this.request<UserProgramme>(
      `/admin/programmes/${programmeId}/enrollments/${enrollmentId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ status }),
      }
    );
  }

  async getProgrammeEnrollments(
    programmeId: string,
    params?: {
      page?: number;
      page_size?: number;
      sort_by?: "username" | "role" | "enrolled_at" | "status" | "current_week";
      sort_order?: "asc" | "desc";
      q?: string;
    }
  ): Promise<Page<EnrollmentWithUser>> {
    const queryParams = new URLSearchParams();

    if (params?.page !== undefined) queryParams.append("page", params.page.toString());
    if (params?.page_size !== undefined)
      queryParams.append("page_size", params.page_size.toString());
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.q) queryParams.append("q", params.q);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/admin/programmes/${programmeId}/enrollments?${queryString}`
      : `/admin/programmes/${programmeId}/enrollments`;

    return this.request<Page<EnrollmentWithUser>>(endpoint);
  }

  async getAvailableUsersForProgramme(programmeId: string): Promise<AvailableUser[]> {
    return this.request<AvailableUser[]>(`/admin/programmes/${programmeId}/available-users`);
  }

  async getCohortAssessors(programmeId: string): Promise<CohortAssessor[]> {
    return this.request<CohortAssessor[]>(`/admin/programmes/${programmeId}/assessors`);
  }

  async addCohortAssessor(programmeId: string, userId: string): Promise<CohortAssessor> {
    return this.request<CohortAssessor>(`/admin/programmes/${programmeId}/assessors`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId }),
    });
  }

  async removeCohortAssessor(programmeId: string, userId: string): Promise<void> {
    return this.request<void>(`/admin/programmes/${programmeId}/assessors/${userId}`, {
      method: "DELETE",
    });
  }

  async getEligibleAssessors(programmeId: string): Promise<EligibleAssessor[]> {
    return this.request<EligibleAssessor[]>(`/admin/programmes/${programmeId}/eligible-assessors`);
  }

  async enrollUserInProgramme(programmeId: string, userId: string): Promise<UserProgramme> {
    return this.request<UserProgramme>(`/admin/programmes/${programmeId}/enroll`, {
      method: "POST",
      body: JSON.stringify({ user_id: userId, programme_id: programmeId }),
    });
  }

  async removeUserFromProgramme(programmeId: string, enrollmentId: string): Promise<void> {
    return this.request<void>(`/admin/programmes/${programmeId}/enrollments/${enrollmentId}`, {
      method: "DELETE",
    });
  }

  // Legacy alias for backward compatibility
  async getPrograms(): Promise<Page<Programme>> {
    return this.getProgrammes();
  }

  // Sources
  async getSources(params?: {
    active_only?: boolean;
    category?: string;
    source_type?: string;
    page?: number;
    page_size?: number;
    q?: string;
    sort_by?: string;
    sort_order?: "asc" | "desc";
  }): Promise<Page<Source>> {
    const qs = new URLSearchParams();
    if (params?.active_only) qs.set("active_only", "true");
    if (params?.category) qs.set("category", params.category);
    if (params?.source_type) qs.set("source_type", params.source_type);
    if (params?.page != null) qs.set("page", String(params.page));
    if (params?.page_size != null) qs.set("page_size", String(params.page_size));
    if (params?.q) qs.set("q", params.q);
    if (params?.sort_by) qs.set("sort_by", params.sort_by);
    if (params?.sort_order) qs.set("sort_order", params.sort_order);
    const query = qs.toString();
    return this.request(`/admin/sources/${query ? `?${query}` : ""}`);
  }

  async createSource(data: Partial<Source>): Promise<Source> {
    return this.request<Source>("/admin/sources/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateSource(id: string, data: Partial<Source>): Promise<Source> {
    return this.request<Source>(`/admin/sources/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteSource(id: string): Promise<void> {
    return this.request<void>(`/admin/sources/${id}`, { method: "DELETE" });
  }

  // Site content — FAQ entries and people (KC-459)
  async getAdminFaqs(): Promise<FaqEntry[]> {
    return this.request<FaqEntry[]>("/admin/faqs");
  }

  async createFaq(data: {
    question: string;
    answer: string;
    is_published: boolean;
  }): Promise<FaqEntry> {
    return this.request<FaqEntry>("/admin/faqs", { method: "POST", body: JSON.stringify(data) });
  }

  async updateFaq(id: string, data: Partial<FaqEntry>): Promise<FaqEntry> {
    return this.request<FaqEntry>(`/admin/faqs/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteFaq(id: string): Promise<void> {
    return this.request<void>(`/admin/faqs/${id}`, { method: "DELETE" });
  }

  async moveFaq(id: string, direction: MoveDirection): Promise<FaqEntry[]> {
    return this.request<FaqEntry[]>(`/admin/faqs/${id}/move`, {
      method: "POST",
      body: JSON.stringify({ direction }),
    });
  }

  // Unlock screen (KC-498)

  async getAdminUnlockScreen(): Promise<UnlockScreenContent> {
    return this.request<UnlockScreenContent>("/admin/unlock-screen");
  }

  async updateUnlockScreen(data: UnlockScreenContent): Promise<UnlockScreenContent> {
    return this.request<UnlockScreenContent>("/admin/unlock-screen", {
      method: "PUT",
      body: JSON.stringify(data),
    });
  }

  async getUnlockScreen(): Promise<UnlockScreenContent> {
    return this.request<UnlockScreenContent>("/unlock-screen");
  }

  async dismissUnlockScreen(
    userProgrammeId: string
  ): Promise<{ id: string; unlock_screen_dismissed_at: string }> {
    return this.request(`/unlock-screen/dismiss/${userProgrammeId}`, { method: "POST" });
  }

  async getFreemiumDiscount(): Promise<FreemiumDiscount> {
    return this.request<FreemiumDiscount>("/payments/freemium-discount");
  }

  async getPublicFaqs(): Promise<PublicFaq[]> {
    return this.request<PublicFaq[]>("/public/faqs");
  }

  async getAdminPeople(group?: PersonGroup): Promise<Person[]> {
    const qs = group ? `?group=${group}` : "";
    return this.request<Person[]>(`/admin/people${qs}`);
  }

  async createPerson(data: PersonPayload): Promise<Person> {
    return this.request<Person>("/admin/people", { method: "POST", body: JSON.stringify(data) });
  }

  async updatePerson(id: string, data: Partial<PersonPayload>): Promise<Person> {
    return this.request<Person>(`/admin/people/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deletePerson(id: string): Promise<void> {
    return this.request<void>(`/admin/people/${id}`, { method: "DELETE" });
  }

  async movePerson(id: string, direction: MoveDirection): Promise<Person[]> {
    return this.request<Person[]>(`/admin/people/${id}/move`, {
      method: "POST",
      body: JSON.stringify({ direction }),
    });
  }

  async uploadPersonPhoto(id: string, file: File): Promise<Person> {
    const fd = new FormData();
    fd.append("file", file);
    const token = this.getToken ? await this.getToken() : null;
    const res = await fetch(`${this.baseURL}/admin/people/${id}/photo`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Failed to upload photo");
    }
    return res.json();
  }

  // Seed Events
  async getSeedEvents(params?: {
    status?: SeedEventStatus;
    event_type?: SeedEventType;
    page?: number;
    page_size?: number;
    q?: string;
    sort_by?: string;
    sort_order?: "asc" | "desc";
  }): Promise<Page<SeedEvent>> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.event_type) qs.set("event_type", params.event_type);
    if (params?.page != null) qs.set("page", String(params.page));
    if (params?.page_size != null) qs.set("page_size", String(params.page_size));
    if (params?.q) qs.set("q", params.q);
    if (params?.sort_by) qs.set("sort_by", params.sort_by);
    if (params?.sort_order) qs.set("sort_order", params.sort_order);
    const query = qs.toString();
    return this.request(`/admin/seed-events/${query ? `?${query}` : ""}`);
  }

  async createSeedEvent(data: Partial<SeedEvent>): Promise<SeedEvent> {
    return this.request<SeedEvent>("/admin/seed-events/", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  }

  async updateSeedEvent(id: string, data: Partial<SeedEvent>): Promise<SeedEvent> {
    return this.request<SeedEvent>(`/admin/seed-events/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  }

  async deleteSeedEvent(id: string): Promise<SeedEvent> {
    return this.request<SeedEvent>(`/admin/seed-events/${id}`, { method: "DELETE" });
  }

  // Tags
  async getTags(tagGroup?: string): Promise<TagResponse[]> {
    const qs = tagGroup ? `?tag_group=${tagGroup}` : "";
    return this.request<TagResponse[]>(`/admin/topics/tags${qs}`);
  }

  async getEvidenceRichness(
    angleTypeIds: string[],
    difficultyId: string | null
  ): Promise<{ value: string | null; display_name: string | null }> {
    const params = new URLSearchParams();
    if (angleTypeIds.length > 0) params.set("angle_type_ids", angleTypeIds.join(","));
    if (difficultyId) params.set("difficulty_id", difficultyId);
    return this.request(`/admin/topics/tags/evidence-richness?${params.toString()}`);
  }

  // Topics
  async getTopics(params?: {
    page?: number;
    pageSize?: number;
    topicAreaId?: string;
    status?: "draft" | "ready" | "assigned";
    q?: string;
    sort_by?: "title" | "created_at";
    sort_order?: "asc" | "desc";
    includeDrafts?: boolean;
    topicArea?: string;
    angleType?: string;
    difficulty?: string;
    evidenceRichness?: string;
    timeMode?: string;
  }): Promise<Page<Topic>> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.pageSize) queryParams.append("page_size", params.pageSize.toString());
    if (params?.topicAreaId) queryParams.append("topic_area_id", params.topicAreaId.toString());
    if (params?.status) queryParams.append("status", params.status);
    if (params?.q) queryParams.append("q", params.q);
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.includeDrafts) queryParams.append("include_drafts", "true");
    if (params?.topicArea) queryParams.append("topic_area", params.topicArea);
    if (params?.angleType) queryParams.append("angle_type", params.angleType);
    if (params?.difficulty) queryParams.append("difficulty", params.difficulty);
    if (params?.evidenceRichness) queryParams.append("evidence_richness", params.evidenceRichness);
    if (params?.timeMode) queryParams.append("time_mode", params.timeMode);
    const queryString = queryParams.toString();
    return this.request<Page<Topic>>(`/admin/topics/${queryString ? `?${queryString}` : ""}`);
  }

  async createTopic(data: {
    title: string;
    description: string;
    task_status?: string;
    topic_area_ids?: string[];
    tag_ids?: string[];
    time_mode_id?: string | null;
    difficulty_id?: string | null;
    archetype_id?: string | null;
    context?: string | null;
    core_question?: string | null;
    guidance?: string | null;
    evidence_hints?: string | null;
  }): Promise<Topic> {
    return this.request<Topic>("/admin/topics/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateTopic(
    topicId: string,
    data: {
      title?: string;
      description?: string;
      task_status?: string;
      topic_area_ids?: string[];
      tag_ids?: string[];
      time_mode_id?: string | null;
      difficulty_id?: string | null;
      evidence_richness_id?: string | null;
      context?: string | null;
      core_question?: string | null;
      guidance?: string | null;
      evidence_hints?: string | null;
      archetype_id?: string | null;
    }
  ): Promise<Topic> {
    return this.request<Topic>(`/admin/topics/${topicId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async deleteTopic(topicId: string): Promise<void> {
    return this.request<void>(`/admin/topics/${topicId}`, {
      method: "DELETE",
    });
  }

  // Topic Generation
  async generateTopics(request: TopicGenerateRequest): Promise<GenerationJob> {
    return this.request<GenerationJob>("/admin/topics/generate", {
      method: "POST",
      body: JSON.stringify(request),
    });
  }

  async generateFromSeedEvent(seedEventId: string, count = 1): Promise<GenerationJob> {
    return this.generateTopics({ seed_event_id: seedEventId, count });
  }

  async getGenerationJob(jobId: string): Promise<GenerationJob> {
    return this.request<GenerationJob>(`/admin/topics/generate/${jobId}`);
  }

  async getGenerationJobs(params?: { status?: string; page?: number; pageSize?: number }): Promise<{
    items: GenerationJob[];
    total: number;
    page: number;
    page_size: number;
    total_pages: number;
  }> {
    const qs = new URLSearchParams();
    if (params?.status) qs.set("status", params.status);
    if (params?.page) qs.set("page", String(params.page));
    if (params?.pageSize) qs.set("page_size", String(params.pageSize));
    const query = qs.toString() ? `?${qs}` : "";
    return this.request(`/admin/topics/jobs${query}`);
  }

  async getDraftTopics(jobId?: string): Promise<Topic[]> {
    const params = jobId ? `?job_id=${jobId}` : "";
    return this.request<Topic[]>(`/admin/topics/drafts${params}`);
  }

  async bulkApproveTopics(topicIds: string[]): Promise<{ approved: number }> {
    return this.request<{ approved: number }>("/admin/topics/bulk-approve", {
      method: "POST",
      body: JSON.stringify({ topic_ids: topicIds }),
    });
  }

  async approveTopic(topicId: string): Promise<Topic> {
    return this.request<Topic>(`/admin/topics/${topicId}/approve`, {
      method: "POST",
    });
  }

  async rejectTopic(topicId: string, rejectionReason: string): Promise<Topic> {
    return this.request<Topic>(`/admin/topics/${topicId}/reject`, {
      method: "POST",
      body: JSON.stringify({ rejection_reason: rejectionReason }),
    });
  }

  async getArchetypes(): Promise<Archetype[]> {
    return this.request<Archetype[]>("/admin/topics/archetypes");
  }

  // Assignments
  async getAssignmentsByProgramme(programmeId: string): Promise<Assignment[]> {
    return this.request<Assignment[]>(`/admin/programmes/${programmeId}/assignments`);
  }

  // Legacy alias
  async getAssignmentsByProgram(programId: string): Promise<Assignment[]> {
    return this.getAssignmentsByProgramme(programId);
  }

  async createAssignment(data: {
    programme_id: string;
    // week_number / assignment_number are server-derived (KC-374).
    start_date: string;
    deadline: string;
  }): Promise<Assignment> {
    return this.request<Assignment>("/admin/assignments/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateAssignment(
    assignmentId: string,
    data: Partial<{
      start_date: string;
      deadline: string;
    }>
  ): Promise<Assignment> {
    return this.request<Assignment>(`/admin/assignments/${assignmentId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // Timeline (for user programme view)
  async getTimeline(programmeId: string, userId: string): Promise<TimelineResponse> {
    return this.request<TimelineResponse>(`/programmes/${programmeId}/timeline?user_id=${userId}`);
  }

  async deleteAssignment(assignmentId: string): Promise<void> {
    return this.request<void>(`/admin/assignments/${assignmentId}`, {
      method: "DELETE",
    });
  }

  async getAllAssignments(params?: {
    page?: number;
    page_size?: number;
    q?: string;
    sort_by?: string;
    sort_order?: "asc" | "desc";
  }): Promise<Page<Assignment>> {
    const qs = new URLSearchParams();
    if (params?.page != null) qs.set("page", String(params.page));
    if (params?.page_size != null) qs.set("page_size", String(params.page_size));
    if (params?.q) qs.set("q", params.q);
    if (params?.sort_by) qs.set("sort_by", params.sort_by);
    if (params?.sort_order) qs.set("sort_order", params.sort_order);
    const query = qs.toString();
    return this.request<Page<Assignment>>(`/admin/assignments/${query ? `?${query}` : ""}`);
  }

  async getAssignment(assignmentId: string): Promise<Assignment> {
    return this.request<Assignment>(`/programmes/assignments/${assignmentId}`);
  }

  // Submissions
  async getSubmissionsByUser(): Promise<Submission[]> {
    return this.request<Submission[]>("/submissions/");
  }

  async getSubmission(submissionId: string): Promise<Submission> {
    return this.request<Submission>(`/submissions/${submissionId}`);
  }

  async getSubmissionFileUrl(submissionId: string): Promise<string> {
    const data = await this.request<{ url: string }>(`/submissions/${submissionId}/file-url`);
    return data.url;
  }

  async getSubmissionByAssignment(assignmentId: string): Promise<Submission | null> {
    try {
      return await this.request<Submission>(`/submissions/assignment/${assignmentId}`);
    } catch (error) {
      // Return null if submission not found (404)
      if (error instanceof Error && (error as any).status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getSubmissionByUserAssignment(userAssignmentId: string): Promise<Submission | null> {
    try {
      return await this.request<Submission>(`/submissions/user-assignment/${userAssignmentId}`);
    } catch (error) {
      // Return null if submission not found (404)
      if (error instanceof Error && (error as any).status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getAllSubmissionsByUserAssignment(userAssignmentId: string): Promise<Submission[]> {
    try {
      return await this.request<Submission[]>(
        `/submissions/user-assignment/${userAssignmentId}/all`
      );
    } catch (error) {
      if (error instanceof Error && (error as any).status === 404) {
        return [];
      }
      throw error;
    }
  }

  async createSubmission(data: {
    user_assignment_id: string;
    topic_id: string;
    file_path: string;
  }): Promise<Submission> {
    return this.request<Submission>("/submissions/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  // Reviews
  async getReview(reviewId: string): Promise<Review> {
    return this.request<Review>(`/reviews/${reviewId}`);
  }

  async getReviewAudit(reviewId: string): Promise<ReviewAuditEntry[]> {
    return this.request<ReviewAuditEntry[]>(`/reviews/${reviewId}/audit`);
  }

  async getTopic(topicId: string): Promise<Topic> {
    return this.request<Topic>(`/admin/topics/${topicId}`);
  }

  async getReviewStatus(
    reviewId: string
  ): Promise<{ status: string; error_message: string | null }> {
    return this.request<{ status: string; error_message: string | null }>(
      `/reviews/${reviewId}/status`
    );
  }

  async retryReview(reviewId: string): Promise<void> {
    return this.request<void>(`/reviews/${reviewId}/retry`, { method: "POST" });
  }

  async getReviewBySubmission(submissionId: string): Promise<Review | null> {
    try {
      return await this.request<Review>(`/reviews/submission/${submissionId}`);
    } catch (error) {
      // Return null if review not found (404)
      if (error instanceof Error && (error as any).status === 404) {
        return null;
      }
      throw error;
    }
  }

  async deleteSubmission(submissionId: string): Promise<void> {
    return this.request<void>(`/submissions/${submissionId}`, {
      method: "DELETE",
    });
  }

  async createReview(data: {
    submission_id: string;
    sections?: Record<string, any>;
    final_score: number;
  }): Promise<Review> {
    return this.request<Review>("/reviews/", {
      method: "POST",
      body: JSON.stringify(data),
    });
  }

  async updateReview(reviewId: string, data: Partial<Review>): Promise<Review> {
    return this.request<Review>(`/reviews/${reviewId}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async submitForHumanReview(reviewId: string): Promise<{ message: string; review_id: string }> {
    return this.request(`/reviews/${reviewId}/submit-for-review`, {
      method: "POST",
    });
  }

  async commitAdminReview(
    reviewId: string,
    data: { admin_sections: Record<string, any>; admin_final_score: number }
  ): Promise<Review> {
    return this.request<Review>(`/reviews/${reviewId}/admin-review`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async getAdminReviews(params?: {
    programme_id?: string;
    status?: string;
    week_number?: number;
    assignment_number?: number;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    q?: string;
  }): Promise<Page<AdminReviewListItem>> {
    const queryParams = new URLSearchParams();
    if (params?.programme_id) queryParams.append("programme_id", params.programme_id.toString());
    if (params?.status) queryParams.append("status", params.status);
    if (params?.week_number != null)
      queryParams.append("week_number", params.week_number.toString());
    if (params?.assignment_number != null)
      queryParams.append("assignment_number", params.assignment_number.toString());
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.page_size) queryParams.append("page_size", params.page_size.toString());
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.q) queryParams.append("q", params.q);
    const queryString = queryParams.toString();
    return this.request<Page<AdminReviewListItem>>(
      `/admin/reviews${queryString ? `?${queryString}` : ""}`
    );
  }

  // User Assignments
  async getUserAssignments(params: {
    programmeId: string;
    weekNumber?: number;
    assignmentNumber?: number;
  }): Promise<AssignmentMappingResponse[]> {
    const queryParams = new URLSearchParams({
      programme_id: params.programmeId.toString(),
    });
    if (params.weekNumber !== undefined) {
      queryParams.append("week_number", params.weekNumber.toString());
    }
    if (params.assignmentNumber !== undefined) {
      queryParams.append("assignment_number", params.assignmentNumber.toString());
    }
    return this.request<AssignmentMappingResponse[]>(
      `/admin/user-assignments/?${queryParams.toString()}`
    );
  }

  async createUserAssignmentsBulk(assignmentId: string): Promise<{
    created: number;
    skipped: number;
    total_enrolled: number;
  }> {
    return this.request(`/admin/user-assignments/bulk`, {
      method: "POST",
      body: JSON.stringify({ assignment_id: assignmentId }),
    });
  }

  async updateUserAssignment(
    id: string,
    data: { topic_id?: string; status?: string }
  ): Promise<UserAssignment> {
    return this.request<UserAssignment>(`/admin/user-assignments/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async assignTopicToUser(
    userProgrammeId: string,
    assignmentId: string,
    topicId: string
  ): Promise<UserAssignment> {
    return this.request<UserAssignment>(
      `/admin/user-assignments/assign-topic?user_programme_id=${userProgrammeId}&assignment_id=${assignmentId}&topic_id=${topicId}`,
      { method: "POST" }
    );
  }

  async getUserAssignmentByAssignment(assignmentId: string): Promise<{
    id: string;
    user_programme_id: string;
    assignment_id: string;
    topic_id: string | null;
    status: string;
    created_at: string;
    updated_at: string;
    topic: { id: string; title: string; description: string } | null;
  } | null> {
    try {
      return await this.request(
        `/programmes/user-assignment/by-assignment?assignment_id=${assignmentId}`
      );
    } catch (error) {
      if (error instanceof Error && (error as any).status === 404) {
        return null;
      }
      throw error;
    }
  }

  // Notifications
  async getNotifications(options?: {
    limit?: number;
    unreadOnly?: boolean;
  }): Promise<NotificationListResponse> {
    const params = new URLSearchParams();
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.unreadOnly) params.append("unread_only", "true");
    const queryString = params.toString();
    return this.request<NotificationListResponse>(
      `/notifications/${queryString ? `?${queryString}` : ""}`
    );
  }

  async markNotificationRead(notificationId: string): Promise<Notification> {
    return this.request<Notification>(`/notifications/${notificationId}/read`, {
      method: "POST",
    });
  }

  async markAllNotificationsRead(): Promise<{ marked_read: number }> {
    return this.request<{ marked_read: number }>(`/notifications/read-all`, {
      method: "POST",
    });
  }

  // Admin Dashboard
  // The unpaginated cohort-switcher list. Its own path segment (not the
  // trailing-slash twin of the paginated `/admin/programmes/` table) so the two
  // can't be confused at a call site — see KC-388.
  async getAdminProgrammes(): Promise<AdminProgrammeItem[]> {
    return this.request("/admin/programme-options");
  }

  async getAdminDashboard(programmeId: string): Promise<{
    programme: {
      id: string;
      name: string;
      status: string;
      start_date: string;
      end_date: string;
      total_weeks: number;
      current_week: number;
      enrolled_users: number;
    };
    topic_inventory: {
      ready: number;
      used: number;
      draft: number;
      projected_need: number;
      shortfall: number;
    };
    current_window: {
      assignment_id: string;
      week_number: number;
      assignment_number: number;
      start_date: string;
      deadline: string;
      time_status: string;
      assigned_count: number;
      total_enrolled: number;
      users_need_topics: number;
      submitted_count: number;
    } | null;
    next_window: {
      assignment_id: string;
      week_number: number;
      assignment_number: number;
      start_date: string;
      deadline: string;
      time_status: string;
      assigned_count: number;
      total_enrolled: number;
      users_need_topics: number;
      submitted_count: number;
    } | null;
    timeline: Array<{
      week_number: number;
      assignment_number: number;
      assignment_id: string;
      start_date: string;
      deadline: string;
      time_status: string;
      assigned_count: number;
      submitted_count: number;
      total_enrolled: number;
    }>;
  }> {
    return this.request(`/admin/dashboard/${programmeId}`);
  }

  async previewAutoAssign(
    assignmentId: string,
    topicAreaId?: string
  ): Promise<{
    assignments: Array<{
      user_assignment_id: string;
      user_id: string;
      username: string;
      topic_id: string;
      topic_title: string;
      topic_created_at: string;
    }>;
    ready_topics_available: number;
    users_need_topics: number;
  }> {
    return this.request("/admin/auto-assign/preview", {
      method: "POST",
      body: JSON.stringify({
        assignment_id: assignmentId,
        topic_area_id: topicAreaId || null,
      }),
    });
  }

  async autoAssignTopics(
    assignmentId: string,
    topicAreaId?: string
  ): Promise<{
    assigned_count: number;
    assignments: Array<{
      user_assignment_id: string;
      user_id: string;
      username: string;
      topic_id: string;
      topic_title: string;
      topic_created_at: string;
    }>;
  }> {
    return this.request("/admin/auto-assign", {
      method: "POST",
      body: JSON.stringify({
        assignment_id: assignmentId,
        topic_area_id: topicAreaId || null,
      }),
    });
  }

  // Payments
  async createCheckoutSession(programmeId: string): Promise<CreateCheckoutSessionResponse> {
    return this.request<CreateCheckoutSessionResponse>("/payments/create-checkout-session", {
      method: "POST",
      body: JSON.stringify({ programme_id: programmeId }),
    });
  }

  async createGuestCheckoutSession(
    programmeId: string,
    email: string
  ): Promise<CreateCheckoutSessionResponse> {
    return this.request<CreateCheckoutSessionResponse>("/payments/create-checkout-session-guest", {
      method: "POST",
      body: JSON.stringify({ programme_id: programmeId, email }),
    });
  }

  async redeemEnrollmentCode(code: string): Promise<RedeemCodeResponse> {
    return this.request<RedeemCodeResponse>("/payments/redeem-code", {
      method: "POST",
      body: JSON.stringify({ code }),
    });
  }

  async getPaymentBySession(sessionId: string): Promise<Payment> {
    return this.request<Payment>(`/payments/session/${sessionId}`);
  }

  async getMyPayments(): Promise<Payment[]> {
    return this.request<Payment[]>("/payments/my-payments");
  }

  async getPaymentReceiptUrl(
    paymentId: string
  ): Promise<{ receipt_url: string; payment_id: string }> {
    return this.request<{ receipt_url: string; payment_id: string }>(
      `/payments/${paymentId}/receipt-url`
    );
  }

  async getPaymentCheckoutUrl(
    paymentId: string
  ): Promise<{ checkout_url: string; payment_id: string; expires_at: number }> {
    return this.request<{ checkout_url: string; payment_id: string; expires_at: number }>(
      `/payments/${paymentId}/checkout-url`
    );
  }

  // Website Forms (Admin)
  async getWaitlistEntries(params?: ListParams): Promise<Page<WaitlistEntry>> {
    return this.request<Page<WaitlistEntry>>(`/admin/waitlist${buildListQuery(params)}`);
  }

  async getPartnershipApplications(params?: ListParams): Promise<Page<PartnershipApplication>> {
    return this.request<Page<PartnershipApplication>>(
      `/admin/partnerships${buildListQuery(params)}`
    );
  }

  async getContactSubmissions(params?: ListParams): Promise<Page<ContactSubmission>> {
    return this.request<Page<ContactSubmission>>(`/admin/contacts${buildListQuery(params)}`);
  }

  async getNewsletterSubscribers(params?: ListParams): Promise<Page<NewsletterSubscriber>> {
    return this.request<Page<NewsletterSubscriber>>(`/admin/newsletter${buildListQuery(params)}`);
  }

  async getEventPitches(params?: ListParams): Promise<Page<EventPitch>> {
    return this.request<Page<EventPitch>>(`/admin/pitches${buildListQuery(params)}`);
  }

  async getAmbassadorApplications(params?: ListParams): Promise<Page<AmbassadorApplication>> {
    return this.request<Page<AmbassadorApplication>>(`/admin/ambassadors${buildListQuery(params)}`);
  }

  async getDominionDemoAccess(params?: ListParams): Promise<Page<DominionDemoAccess>> {
    return this.request<Page<DominionDemoAccess>>(
      `/admin/dominion-demo-access${buildListQuery(params)}`
    );
  }

  // --- Certificates (KC-455) ---

  async getCertificateRun(programmeId: string): Promise<CertificateRun | null> {
    return this.request<CertificateRun | null>(
      `/admin/certificates/runs?programme_id=${encodeURIComponent(programmeId)}`
    );
  }

  async createCertificateRun(programmeId: string): Promise<CertificateRun> {
    return this.request<CertificateRun>(`/admin/certificates/runs`, {
      method: "POST",
      body: JSON.stringify({ programme_id: programmeId }),
    });
  }

  async getCertificateRunPreview(runId: string): Promise<CertificateRunPreview> {
    return this.request<CertificateRunPreview>(`/admin/certificates/runs/${runId}/preview`);
  }

  async certificateTestSend(runId: string, userProgrammeId?: string): Promise<CertificateRun> {
    return this.request<CertificateRun>(`/admin/certificates/runs/${runId}/test-send`, {
      method: "POST",
      body: JSON.stringify({ user_programme_id: userProgrammeId ?? null }),
    });
  }

  async approveCertificateRun(runId: string): Promise<CertificateRun> {
    return this.request<CertificateRun>(`/admin/certificates/runs/${runId}/approve`, {
      method: "POST",
    });
  }

  async authoriseCertificateRun(
    runId: string,
    issueDate?: string
  ): Promise<CertificateAuthoriseResult> {
    return this.request<CertificateAuthoriseResult>(`/admin/certificates/runs/${runId}/authorise`, {
      method: "POST",
      body: JSON.stringify({ issue_date: issueDate ?? null }),
    });
  }

  async getCertificateSendLog(
    runId: string,
    params?: ListParams
  ): Promise<Page<CertificateSendLogItem>> {
    return this.request<Page<CertificateSendLogItem>>(
      `/admin/certificates/runs/${runId}/send-log${buildListQuery(params)}`
    );
  }

  async retryCertificateEmail(scheduledEmailId: string): Promise<{ outcome: string }> {
    return this.request<{ outcome: string }>(
      `/admin/certificates/emails/${scheduledEmailId}/retry`,
      { method: "POST" }
    );
  }

  // --- Participation badges (KC-497) ---

  async getCohortBadges(programmeId: string): Promise<CohortBadges> {
    return this.request<CohortBadges>(
      `/admin/certificates/badges?programme_id=${encodeURIComponent(programmeId)}`
    );
  }

  async getBadgeArtwork(): Promise<BadgeArtwork> {
    return this.request<BadgeArtwork>(`/admin/certificates/badge-artwork`);
  }

  async uploadBadgeArtwork(file: File): Promise<BadgeArtwork> {
    const fd = new FormData();
    fd.append("file", file);
    const token = this.getToken ? await this.getToken() : null;
    const res = await fetch(`${this.baseURL}/admin/certificates/badge-artwork`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Failed to upload artwork");
    }
    return res.json();
  }

  async resetBadgeArtwork(): Promise<BadgeArtwork> {
    return this.request<BadgeArtwork>(`/admin/certificates/badge-artwork`, { method: "DELETE" });
  }

  async getMyCertificates(): Promise<MyCertificate[]> {
    return this.request<MyCertificate[]>(`/certificates/me`);
  }

  async setCertificateVisibility(certificateId: string, visible: boolean): Promise<MyCertificate> {
    return this.request<MyCertificate>(`/certificates/me/${certificateId}/visibility`, {
      method: "PUT",
      body: JSON.stringify({ visible }),
    });
  }

  // Update categorization fields
  async updateWaitlistCategorization(
    id: string,
    data: { cohort?: string | null; contact_status?: string; plan_type?: string | null }
  ): Promise<{
    id: string;
    cohort: string | null;
    contact_status: string;
    plan_type: string | null;
  }> {
    return this.request(`/admin/waitlist/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async updatePartnershipCategorization(
    id: string,
    data: { cohort?: string | null; contact_status?: string; plan_type?: string | null }
  ): Promise<{
    id: string;
    cohort: string | null;
    contact_status: string;
    plan_type: string | null;
  }> {
    return this.request(`/admin/partnerships/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async updateContactCategorization(
    id: string,
    data: { cohort?: string | null; contact_status?: string; plan_type?: string | null }
  ): Promise<{
    id: string;
    cohort: string | null;
    contact_status: string;
    plan_type: string | null;
  }> {
    return this.request(`/admin/contacts/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async updateNewsletterCategorization(
    id: string,
    data: { cohort?: string | null; contact_status?: string; plan_type?: string | null }
  ): Promise<{
    id: string;
    cohort: string | null;
    contact_status: string;
    plan_type: string | null;
  }> {
    return this.request(`/admin/newsletter/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async updatePitchCategorization(
    id: string,
    data: { cohort?: string | null; contact_status?: string; plan_type?: string | null }
  ): Promise<{
    id: string;
    cohort: string | null;
    contact_status: string;
    plan_type: string | null;
  }> {
    return this.request(`/admin/pitches/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async updateAmbassadorCategorization(
    id: string,
    data: { cohort?: string | null; contact_status?: string; plan_type?: string | null }
  ): Promise<{
    id: string;
    cohort: string | null;
    contact_status: string;
    plan_type: string | null;
  }> {
    return this.request(`/admin/ambassadors/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  async updateDominionDemoAccessCategorization(
    id: string,
    data: { cohort?: string | null; contact_status?: string; plan_type?: string | null }
  ): Promise<{
    id: string;
    cohort: string | null;
    contact_status: string;
    plan_type: string | null;
  }> {
    return this.request(`/admin/dominion-demo-access/${id}`, {
      method: "PATCH",
      body: JSON.stringify(data),
    });
  }

  // CSV Export methods with authentication
  private async downloadCSV(endpoint: string): Promise<{ blob: Blob; filename: string }> {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getToken ? await this.getToken() : null;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      throw new Error("Failed to export CSV");
    }

    // Extract filename from Content-Disposition header
    let filename = `export-${new Date().toISOString().split("T")[0]}.csv`;
    const contentDisposition = response.headers.get("Content-Disposition");
    if (contentDisposition) {
      // Handle both encoded (filename*) and regular filename
      const encodedMatch = contentDisposition.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);
      const regularMatch = contentDisposition.match(/filename\s*=\s*(?:"([^"]+)"|([^;]+))/i);

      if (encodedMatch?.[1]) {
        try {
          filename = decodeURIComponent(encodedMatch[1].trim());
        } catch {
          filename = encodedMatch[1].trim();
        }
      } else if (regularMatch) {
        filename = (regularMatch[1] || regularMatch[2]).trim();
      }
    }

    const blob = await response.blob();
    return { blob, filename };
  }

  async exportWaitlistCSV(): Promise<{ blob: Blob; filename: string }> {
    return this.downloadCSV("/admin/waitlist/export");
  }

  async exportPartnershipsCSV(): Promise<{ blob: Blob; filename: string }> {
    return this.downloadCSV("/admin/partnerships/export");
  }

  async exportContactsCSV(): Promise<{ blob: Blob; filename: string }> {
    return this.downloadCSV("/admin/contacts/export");
  }

  async exportNewsletterCSV(): Promise<{ blob: Blob; filename: string }> {
    return this.downloadCSV("/admin/newsletter/export");
  }

  async exportPitchesCSV(): Promise<{ blob: Blob; filename: string }> {
    return this.downloadCSV("/admin/pitches/export");
  }

  async exportAmbassadorsCSV(): Promise<{ blob: Blob; filename: string }> {
    return this.downloadCSV("/admin/ambassadors/export");
  }

  async exportDominionDemoAccessCSV(): Promise<{ blob: Blob; filename: string }> {
    return this.downloadCSV("/admin/dominion-demo-access/export");
  }

  async exportCohortUsersCSV(programmeId: string): Promise<{ blob: Blob; filename: string }> {
    return this.downloadCSV(`/admin/programmes/${programmeId}/enrollments/export`);
  }

  // Support Requests
  async submitSupportRequest(payload: SupportRequestCreatePayload): Promise<SupportRequest> {
    return this.request<SupportRequest>("/support/requests", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  async listAdminSupportRequests(params?: {
    status?: SupportRequestStatus;
    severity?: SupportRequestSeverity;
    unowned?: boolean;
    page?: number;
    page_size?: number;
    sort_by?: string;
    sort_order?: "asc" | "desc";
    q?: string;
  }): Promise<Page<SupportRequestAdminItem>> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append("status", params.status);
    if (params?.severity) queryParams.append("severity", params.severity);
    if (params?.unowned) queryParams.append("unowned", "true");
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.page_size) queryParams.append("page_size", params.page_size.toString());
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.q) queryParams.append("q", params.q);
    const qs = queryParams.toString();
    return this.request<Page<SupportRequestAdminItem>>(
      `/admin/support/requests${qs ? `?${qs}` : ""}`
    );
  }

  async getAdminSupportRequest(id: string): Promise<SupportRequestAdminItem> {
    return this.request<SupportRequestAdminItem>(`/admin/support/requests/${id}`);
  }

  async updateAdminSupportRequest(
    id: string,
    patch: {
      status?: SupportRequestStatus;
      severity?: SupportRequestSeverity;
      resolution?: SupportRequestResolution;
      linked_issue?: string | null;
      admin_notes?: string | null;
    }
  ): Promise<SupportRequestAdminItem> {
    return this.request<SupportRequestAdminItem>(`/admin/support/requests/${id}`, {
      method: "PATCH",
      body: JSON.stringify(patch),
    });
  }

  /** Take ownership of a request from the admin queue (one-shot, like the email link). */
  async claimSupportRequest(id: string): Promise<SupportRequestAdminItem> {
    return this.request<SupportRequestAdminItem>(`/admin/support/requests/${id}/claim`, {
      method: "POST",
    });
  }

  /** Who is alerted about support requests (effective values). */
  async getSupportSettings(): Promise<SupportSettings> {
    return this.request<SupportSettings>("/admin/support/settings");
  }

  /** Change the support recipients. Superadmin only. */
  async updateSupportSettings(patch: SupportSettingsUpdate): Promise<SupportSettings> {
    return this.request<SupportSettings>("/admin/support/settings", {
      method: "PUT",
      body: JSON.stringify(patch),
    });
  }

  /** The authenticated user's own support requests (with any Reply). */
  async listMySupportRequests(): Promise<MySupportRequest[]> {
    return this.request<MySupportRequest[]>("/support/requests");
  }

  /** Send a one-shot, user-visible Reply (admin/superadmin). */
  async replyToSupportRequest(id: string, reply_body: string): Promise<SupportRequestAdminItem> {
    return this.request<SupportRequestAdminItem>(`/admin/support/requests/${id}/reply`, {
      method: "POST",
      body: JSON.stringify({ reply_body }),
    });
  }

  /** Correct an already-sent Reply (superadmin only; no resend). */
  async editSupportReply(id: string, reply_body: string): Promise<SupportRequestAdminItem> {
    return this.request<SupportRequestAdminItem>(`/admin/support/requests/${id}/reply`, {
      method: "PATCH",
      body: JSON.stringify({ reply_body }),
    });
  }

  // Email Templates (admin, read-only catalog)
  async getEmailTemplates(): Promise<EmailTemplateListItem[]> {
    return this.request<EmailTemplateListItem[]>("/admin/email-templates");
  }

  async getEmailTemplate(id: string): Promise<EmailTemplateDetail> {
    return this.request<EmailTemplateDetail>(`/admin/email-templates/${id}`);
  }

  async previewEmailTemplate(
    id: string,
    context: Record<string, string>
  ): Promise<{ rendered_html: string }> {
    return this.request(`/admin/email-templates/${id}/preview`, {
      method: "POST",
      body: JSON.stringify({ context }),
    });
  }

  // Email sequence — schedule (plan), log (audit), suppression + manual ops
  async getEmailSchedule(params?: {
    status?: string;
    template_id?: string;
    user_id?: string;
    programme_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<Page<ScheduledEmailItem>> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== null) qs.append(k, String(v));
    }
    const q = qs.toString();
    return this.request<Page<ScheduledEmailItem>>(`/admin/email-schedule${q ? `?${q}` : ""}`);
  }

  async getEmailLog(params?: {
    status?: string;
    template_id?: string;
    user_id?: string;
    q?: string;
    page?: number;
    page_size?: number;
  }): Promise<Page<EmailLogItem>> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== null) qs.append(k, String(v));
    }
    const q = qs.toString();
    return this.request<Page<EmailLogItem>>(`/admin/email-log${q ? `?${q}` : ""}`);
  }

  async getEmailSuppressions(params?: {
    page?: number;
    page_size?: number;
    q?: string;
  }): Promise<Page<SuppressionItem>> {
    const qs = new URLSearchParams();
    for (const [k, v] of Object.entries(params ?? {})) {
      if (v !== undefined && v !== null) qs.append(k, String(v));
    }
    const q = qs.toString();
    return this.request<Page<SuppressionItem>>(`/admin/email/suppressions${q ? `?${q}` : ""}`);
  }

  async sendScheduledEmailNow(id: string): Promise<{ status: string }> {
    return this.request(`/admin/email-schedule/${id}/send-now`, { method: "POST" });
  }

  async cancelScheduledEmail(id: string): Promise<{ status: string }> {
    return this.request(`/admin/email-schedule/${id}/cancel`, { method: "POST" });
  }

  async resendEmailLog(id: string): Promise<{ status: string }> {
    return this.request(`/admin/email-log/${id}/resend`, { method: "POST" });
  }

  async adHocSendEmail(payload: {
    template_id: string;
    context?: Record<string, unknown>;
    user_id?: string;
    programme_id?: string;
  }): Promise<{ queued: number }> {
    return this.request("/admin/email/ad-hoc", { method: "POST", body: JSON.stringify(payload) });
  }

  async unsuppressEmail(email: string): Promise<{ status: string }> {
    return this.request(`/admin/email/suppressions/${encodeURIComponent(email)}`, {
      method: "DELETE",
    });
  }

  async getParticipantEmailTimeline(userId: string): Promise<EmailTimelineResponse> {
    return this.request<EmailTimelineResponse>(`/admin/participants/${userId}/email-timeline`);
  }

  async getParticipantCohorts(userId: string): Promise<ParticipantCohortItem[]> {
    return this.request<ParticipantCohortItem[]>(`/admin/participants/${userId}/cohorts`);
  }

  async getParticipantTopics(userId: string): Promise<ParticipantTopicItem[]> {
    return this.request<ParticipantTopicItem[]>(`/admin/participants/${userId}/topics`);
  }

  // Assessment Fixtures (admin)
  async listAssessmentFixtures(params?: {
    page?: number;
    page_size?: number;
    sort_by?: "name" | "topic_title" | "created_at" | "updated_at";
    sort_order?: "asc" | "desc";
    q?: string;
  }): Promise<Page<AssessmentFixture>> {
    const queryParams = new URLSearchParams();
    if (params?.page !== undefined) queryParams.append("page", params.page.toString());
    if (params?.page_size !== undefined)
      queryParams.append("page_size", params.page_size.toString());
    if (params?.sort_by) queryParams.append("sort_by", params.sort_by);
    if (params?.sort_order) queryParams.append("sort_order", params.sort_order);
    if (params?.q) queryParams.append("q", params.q);

    const queryString = queryParams.toString();
    const endpoint = queryString
      ? `/admin/assessment-fixtures?${queryString}`
      : "/admin/assessment-fixtures";
    return this.request<Page<AssessmentFixture>>(endpoint);
  }

  async promoteReviewToFixture(
    reviewId: string,
    payload: { name?: string | null; require_admin_score?: boolean } = {}
  ): Promise<AssessmentFixture> {
    // Hand-rolled (not via `request`) so the 409 response body — which carries
    // the existing fixture id under `detail.fixture_id` — survives error handling.
    const token = this.getToken ? await this.getToken() : null;
    const res = await fetch(`${this.baseURL}/admin/reviews/${reviewId}/promote-to-fixture`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({
        name: payload.name ?? null,
        require_admin_score: payload.require_admin_score ?? true,
      }),
    });
    if (res.ok) return (await res.json()) as AssessmentFixture;
    const body = await res.json().catch(() => ({}));
    const err = new Error(
      typeof body.detail === "string" ? body.detail : body.detail?.message || "Promotion failed"
    ) as Error & { status: number; existingFixtureId?: string };
    err.status = res.status;
    if (res.status === 409 && body.detail?.fixture_id) {
      err.existingFixtureId = body.detail.fixture_id as string;
    }
    throw err;
  }

  async getAssessmentFixture(id: string): Promise<AssessmentFixtureWithRuns> {
    return this.request<AssessmentFixtureWithRuns>(`/admin/assessment-fixtures/${id}`);
  }

  async createAssessmentFixture(
    payload: {
      name: string;
      topic_title: string;
      topic_description: string;
      article_pdf: File;
      notes?: string | null;
    },
    runImmediately = false
  ): Promise<{ fixture: AssessmentFixture; run_id: string | null }> {
    const qs = runImmediately ? "?run=true" : "";
    const fd = new FormData();
    fd.append("name", payload.name);
    fd.append("topic_title", payload.topic_title);
    fd.append("topic_description", payload.topic_description);
    fd.append("article_pdf", payload.article_pdf);
    if (payload.notes) fd.append("notes", payload.notes);

    const token = this.getToken ? await this.getToken() : null;
    const res = await fetch(`${this.baseURL}/admin/assessment-fixtures${qs}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Failed to create fixture");
    }
    return res.json();
  }

  async updateAssessmentFixture(
    id: string,
    patch: Partial<{
      name: string;
      topic_title: string;
      topic_description: string;
      notes: string | null;
    }>
  ): Promise<AssessmentFixture> {
    return this.request<AssessmentFixture>(`/admin/assessment-fixtures/${id}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    });
  }

  async replaceAssessmentFixtureArticle(id: string, file: File): Promise<AssessmentFixture> {
    const fd = new FormData();
    fd.append("article_pdf", file);
    const token = this.getToken ? await this.getToken() : null;
    const res = await fetch(`${this.baseURL}/admin/assessment-fixtures/${id}/article`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(err.detail || "Failed to replace article");
    }
    return res.json();
  }

  async deleteAssessmentFixture(id: string): Promise<void> {
    return this.request<void>(`/admin/assessment-fixtures/${id}`, {
      method: "DELETE",
    });
  }

  async runAssessmentFixture(
    id: string,
    criterionKey?: string
  ): Promise<{ run_id: string; status: AssessmentRunStatus }> {
    return this.request<{ run_id: string; status: AssessmentRunStatus }>(
      `/admin/assessment-fixtures/${id}/run`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(criterionKey ? { criterion_key: criterionKey } : {}),
      }
    );
  }

  async evalSweepAssessmentFixture(
    id: string,
    n = 5
  ): Promise<{ run_id: string; status: AssessmentRunStatus }[]> {
    return this.request<{ run_id: string; status: AssessmentRunStatus }[]>(
      `/admin/assessment-fixtures/${id}/eval-sweep?n=${n}`,
      { method: "POST" }
    );
  }

  async getAssessmentFixtureEvalSummary(id: string): Promise<AssessmentFixtureEvalSummary> {
    return this.request<AssessmentFixtureEvalSummary>(
      `/admin/assessment-fixtures/${id}/eval-summary`
    );
  }

  async cloneAssessmentFixture(id: string): Promise<AssessmentFixture> {
    return this.request<AssessmentFixture>(`/admin/assessment-fixtures/${id}/clone`, {
      method: "POST",
    });
  }

  async getAssessmentFixtureRun(runId: string): Promise<AssessmentFixtureRun> {
    return this.request<AssessmentFixtureRun>(`/admin/assessment-fixture-runs/${runId}`);
  }
}

export const api = new ApiClient(API_BASE_URL);
